import type {
  EInvoiceCreatePayload,
  EInvoicePayloadLineItem,
} from "@/types/e-invoice-request";

export function formatVND(amount: number): string {
  return new Intl.NumberFormat("vi-VN", {
    style: "currency",
    currency: "VND",
    minimumFractionDigits: 0,
    maximumFractionDigits: 6,
  }).format(amount);
}

/** Payment enum names come from the order; Hub method codes use a different enum. */
export function getInvoicePaymentMethod(method?: string) {
  switch (method) {
    case "QRCode":
      return { methodCode: "2", label: "QR Code" };
    case "BankTransfer":
      return { methodCode: "2", label: "Bank Transfer" };
    case "Balance":
      // Hub has no wallet code; retain its general cash/transfer classification.
      return { methodCode: "3", label: "Account Balance" };
    default:
      return { methodCode: "3", label: method || "Not available" };
  }
}

export function formatCurrency(amount: number, currencyCode = "VND"): string {
  const locale = currencyCode === "VND" ? "vi-VN" : "en-US";
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currencyCode,
    minimumFractionDigits: 0,
    maximumFractionDigits: 6,
  }).format(amount);
}

export const TAX_RATE_OPTIONS = [
  { value: 10, label: "10%" },
  { value: 8, label: "8%" },
  { value: 5, label: "5%" },
  { value: 0, label: "0%" },
  { value: -1, label: "Undeclared (KCT)" },
  { value: -2, label: "Tax Exempt (KTT)" },
] as const;

export function formatTaxRateLabel(rate: number): string {
  if (rate === -1) return "Undeclared";
  if (rate === -2) return "Tax Exempt";
  return `${rate}%`;
}

const CURRENCY_DECIMAL_PLACES: Record<string, number> = {
  VND: 0,
  JPY: 0,
  KWD: 3,
};

export function roundToCurrency(amount: number, currencyCode = "VND"): number {
  const decimalPlaces =
    CURRENCY_DECIMAL_PLACES[currencyCode.toUpperCase()] ?? 2;
  const factor = 10 ** decimalPlaces;
  if (decimalPlaces === 0) return Math.round(amount);

  // .NET decimal Math.Round uses midpoint-to-even by default. Mirror it here
  // instead of JavaScript's midpoint-toward-positive-infinity Math.round.
  const scaled = amount * factor;
  const lower = Math.floor(scaled);
  const fraction = scaled - lower;
  const rounded =
    Math.abs(fraction - 0.5) < 1e-10
      ? lower % 2 === 0
        ? lower
        : lower + 1
      : Math.round(scaled);
  return rounded / factor;
}

/** Six-decimal intermediates; midpoint-to-even, like .NET decimal rounding. */
export function round6(amount: number): number {
  const scaled = amount * 1_000_000;
  const lower = Math.floor(scaled);
  const midpoint =
    Math.abs(scaled - lower - 0.5) <=
    Number.EPSILON * Math.max(1, Math.abs(scaled));
  return (
    (midpoint ? (lower % 2 === 0 ? lower : lower + 1) : Math.round(scaled)) /
    1_000_000
  );
}

/**
 * Split the discount evenly, cap each share at the line value, and redistribute
 * the remainder to lines with available value. Return lines in their original order.
 */
export function distributeDiscountAcrossLines(
  lines: EInvoicePayloadLineItem[],
  totalDiscount: number,
): EInvoicePayloadLineItem[] {
  if (lines.length === 0) return lines;

  const capacities = lines.map((line) =>
    Math.max(0, round6(line.quantity * round6(line.unitPrice))),
  );
  const subtotal = capacities.reduce((sum, value) => sum + value, 0);
  let remaining = round6(Math.min(Math.max(0, totalDiscount), subtotal));
  const discounts = new Array<number>(lines.length).fill(0);
  const indices = lines
    .map((_, index) => index)
    .sort((a, b) => capacities[a] - capacities[b]);

  indices.forEach((index, position) => {
    const linesLeft = indices.length - position;
    const share = linesLeft === 1 ? remaining : round6(remaining / linesLeft);
    const amount = Math.min(capacities[index], share);
    discounts[index] = amount;
    remaining = Math.max(0, round6(remaining - amount));
  });

  return lines.map((line, index) => ({
    ...line,
    discountPercentage: 0,
    discountAmount: discounts[index],
  }));
}

export function getVatFraction(taxRate: number): number {
  // Preserve legacy negative codes while handling Wi-Invoice's code 99.
  return taxRate > 0 && taxRate !== 99 ? taxRate / 100 : 0;
}

/**
 * Recompute the computed fields of a single invoice line so the payload stays
 * internally consistent after an edit. Intermediate amounts keep six decimals;
 * totals also keep six decimals to match the Hub contract.
 *
 *   totalAmountWithoutVat = max(0, round(quantity * unitPrice) − round(discount))
 *   vatAmount             = round(totalAmountWithoutVat * VAT fraction)
 *   totalAmount           = round(totalAmountWithoutVat + vatAmount)
 */
export function recomputeLine(
  line: EInvoicePayloadLineItem,
  taxRate?: number,
): EInvoicePayloadLineItem {
  const effectiveTaxRate = taxRate ?? line.taxRate ?? 10;
  const unitPrice = round6(line.unitPrice);
  const lineAmount = Math.max(0, round6(line.quantity * unitPrice));
  const discountPercentage = Math.min(
    99.5,
    Math.max(0, line.discountPercentage),
  );
  const discountAmount = Math.min(
    lineAmount,
    Math.max(
      0,
      round6(
        discountPercentage > 0
          ? (lineAmount * discountPercentage) / 100
          : line.discountAmount,
      ),
    ),
  );
  const totalAmountWithoutVat = round6(
    Math.max(0, lineAmount - discountAmount),
  );
  const vatRateFrac = getVatFraction(effectiveTaxRate);
  const vatAmount = round6(totalAmountWithoutVat * vatRateFrac);
  const totalAmount = round6(totalAmountWithoutVat + vatAmount);
  return {
    ...line,
    unitPrice,
    taxRate: effectiveTaxRate,
    discountPercentage,
    discountAmount,
    totalAmountWithoutVat,
    vatAmount,
    totalAmount,
  };
}

/**
 * Recompute every computed field of an e-invoice payload (per-line totals and
 * the payment summary), returning a NEW payload. Order-level VAT rate can be passed
 * explicitly to apply it to every line; otherwise each line keeps its own rate.
 *
 * Payment summary folding:
 *   totalAmountWithoutVat = Σ line.totalAmountWithoutVat + deliveryFee
 *   vatAmount             = Σ line.vatAmount + delivery VAT
 *   totalAmount           = totalAmountWithoutVat + vatAmount
 * Discounts are already included in the line amounts.
 */
export function recomputePayload(
  payload: EInvoiceCreatePayload,
  orderTaxRate?: number,
): EInvoiceCreatePayload {
  const round = round6;
  const details = payload.details.map((item) =>
    recomputeLine(item, orderTaxRate),
  );
  const deliveryFee = round(payload.delivery?.deliveryFee ?? 0);
  // Delivery uses a percentage, unlike line VAT rates with special tax codes.
  const deliveryVat = round(
    (deliveryFee * (payload.delivery?.deliveryPercentage ?? 0)) / 100,
  );

  const totalAmountWithoutVat = round(
    details.reduce(
      (sum, line) => sum + line.totalAmountWithoutVat,
      deliveryFee,
    ),
  );
  const vatAmount = round(
    details.reduce((sum, line) => sum + line.vatAmount, deliveryVat),
  );
  const totalAmount = round(totalAmountWithoutVat + vatAmount);

  return {
    ...payload,
    details,
    delivery: { ...payload.delivery, deliveryFee },
    payment: {
      ...payload.payment,
      // Summary of discounts already applied to the lines, not an extra deduction.
      discountAmount: round(
        details.reduce((sum, line) => sum + line.discountAmount, 0),
      ),
      totalAmountWithoutVat,
      vatAmount,
      totalAmount,
    },
  };
}
